import { ReactElement, ReactNode, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { IIcons, Divider, ButtonGroup, ButtonGroupData } from '@ascd/witsby-components';
import { Box, Container, Grid, SxProps, Theme, useTheme } from '@mui/material';

import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';
import MetaTitle from '@components/MetaTitle';
import PagesSubHeader from '@components/PagesSubHeader';
import { ADMIN_ROUTES, BUTTON_GROUP, SOURCE } from '@constants';

import { AppContext } from '@contexts/appContext';
import { detectDeviceIsMobile, detectDeviceIsTablet } from '@utils';
import { getCookie } from 'cookies-next';

interface IAdminLayout {
  title: string;
  children: ReactNode;
  titleViewBox?: string;
  withSidebar?: boolean;
  titleIcon?: keyof IIcons;
  sideChildren?: ReactNode;
  iconSx?: SxProps<Theme>;
  childrenSx?: SxProps<Theme>;
  handleClose?: () => void;
  breadCrumbsChildren?: ReactElement;
}

const TagsFilterParam = withDefault(ArrayParam, []);

const AdminLayout = ({
  title,
  children,
  titleIcon,
  titleViewBox,
  withSidebar = false,
  sideChildren,
  iconSx,
  childrenSx,
  handleClose = () => null,
  breadCrumbsChildren,
}: IAdminLayout) => {
  const [, setQuery] = useQueryParams({
    listType: TagsFilterParam,
  });

  const theme1 = useTheme();
  const router = useRouter();
  const isMobile = detectDeviceIsMobile();
  const isTablet = detectDeviceIsTablet();

  const {
    state: {
      currentUserRole: { isManager, isAscdAdmin, isInstitutionalAdmin, isAuthor },
    },
  } = useContext(AppContext);

  const activePath = useMemo(() => {
    const splitPathName = router?.pathname.split('/');
    if (splitPathName?.length && splitPathName[2]) return `/${splitPathName[2]}`;
    return router?.pathname?.replace(ADMIN_ROUTES.ADMIN, '');
  }, [router?.pathname]);

  const buttonOptions = [
    ...(isAscdAdmin || isInstitutionalAdmin || isManager
      ? [{ key: '', title: BUTTON_GROUP.DASHBOARD, url: ADMIN_ROUTES.ADMIN }]
      : []),
    ...(isInstitutionalAdmin || isManager
      ? [
          {
            key: '/assignment-progress',
            title: BUTTON_GROUP.GROUP_PROGRESS,
            url: ADMIN_ROUTES.GROUP_PROGRESS,
          },
          { key: '/assignment', title: BUTTON_GROUP.ASSIGNMENT, url: ADMIN_ROUTES.ASSIGNMENT },
          {
            key: '/manage-groups',
            url: ADMIN_ROUTES.MANAGE_GROUPS,
            title: BUTTON_GROUP.MANAGE_GROUPS,
          },
        ]
      : []),
    /** @todo: disable manage-users for now */
    // ...(isInstitutionalAdmin || isAscdAdmin
    //   ? [{ key: '/manage-users', title: BUTTON_GROUP.MANAGE_USERS, url: '/admin/manage-users' }]
    //   : []),
    ...(isInstitutionalAdmin || isAscdAdmin || isAuthor
      ? [{ key: '/authoring', title: BUTTON_GROUP.AUTHORING, url: ADMIN_ROUTES.AUTHORING_DEFAULT }]
      : []),
    ...(isAscdAdmin || isInstitutionalAdmin || isManager
      ? [
          {
            key: '/user-management',
            title: BUTTON_GROUP.MANAGE_USERS,
            url: ADMIN_ROUTES.MANAGE_USERS,
          },
        ]
      : []),
    ...(isAscdAdmin || isInstitutionalAdmin
      ? [{ key: '/settings', title: BUTTON_GROUP.ADMIN_SETTINGS, url: ADMIN_ROUTES.SETTINGS }]
      : []),
  ];

  return (
    <>
      <MetaTitle title={title} />
      <PagesSubHeader
        title={title}
        iconSx={iconSx}
        titleIcon={titleIcon}
        titleViewBox={titleViewBox}
      />

      {!isMobile && (
        <Grid container>
          <Grid item xs={12} md={3} />
          <Grid item xs={12} md={9} data-testid="header-button">
            <ButtonGroup
              size="small"
              activeKey={activePath}
              sx={{ p: { xs: 0 }, mb: 3 }}
              handleClick={(data: ButtonGroupData) => {
                const source = getCookie('source');
                if (data?.url) {
                  if (data.url === ADMIN_ROUTES.MANAGE_USERS) {
                    if (source === SOURCE.SALESFORCE) {
                      window.open(
                        process.env.NEXT_PUBLIC_WITSBY_USER_MANAGEMENT_SALESFORCE_URL,
                        '_blank',
                        'noopener, noreferrer',
                      );
                    } else {
                      window.open(
                        process.env.NEXT_PUBLIC_WITSBY_USER_MANAGEMENT_OKTA_URL,
                        '_blank',
                        'noopener, noreferrer',
                      );
                    }
                  } else {
                    setQuery({ listType: [] }, 'pushIn');
                    router.push(data.url);
                    handleClose();
                  }
                }
              }}
              buttons={buttonOptions}
            />
          </Grid>
        </Grid>
      )}
      {breadCrumbsChildren && (
        <Grid container>
          <Grid item xs={12} sm={4} md={3} order={{ xs: 2, sm: 1 }} />

          <Grid item xs={12} sm={8} md={9} order={{ xs: 1, sm: 2 }}>
            {breadCrumbsChildren}
          </Grid>
        </Grid>
      )}
      <Divider
        sx={{ position: 'relative', top: breadCrumbsChildren ? undefined : 40, width: '100%' }}
      />
      <Container
        maxWidth={false}
        sx={(theme) => ({
          pb: '1.5rem',
          px: '0.688rem',
          display: 'flex',
          [theme.breakpoints.down('sm')]: {
            overflowX: 'hidden',
          },
        })}>
        {withSidebar ? (
          children
        ) : (
          <>
            <Box
              sx={(theme) => ({
                width: isTablet ? '75px ' : 'calc((100vw - 900px)/2)',

                [theme.breakpoints.up('lg')]: {
                  minWidth: '305px',
                },
                [theme.breakpoints.between('md', 'lg')]: {
                  minWidth: '270px',
                },
                [theme.breakpoints.down('md')]: {
                  minWidth: '100px',
                },
                [theme.breakpoints.down('sm')]: {
                  width: '0px',
                  minWidth: '70px',
                },
              })}>
              {sideChildren}
            </Box>
            <Box
              sx={{
                width: isTablet ? 'calc(100vw - (86px*2))' : '867px',
                [theme1.breakpoints.up('lg')]: {
                  minWidth: '867px',
                },
                [theme1.breakpoints.down('lg')]: {
                  minWidth: '650px',
                },
                [theme1.breakpoints.down('sm')]: {
                  minWidth: '100vw',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  justifyContent: 'center',
                  mt: '12px',
                },
                paddingRight: '22px',
                ...childrenSx,
              }}>
              {children}
            </Box>
          </>
        )}
      </Container>
    </>
  );
};

export { AdminLayout };
